import React, { useState, useEffect, useMemo } from 'react';
import { Settings, Users, ChevronDown, ChevronRight, MapPin, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';

const ShiftProSettings = () => {
  const [configs, setConfigs] = useState([]);
  const [users, setUsers] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedTask, setExpandedTask] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/shiftpro/approval-configs');
      setConfigs(res.data.configs || []);
      setUsers(res.data.users || []);
      setOutlets(res.data.outlets || []);
      setTasks(res.data.tasks || []);
    } catch {
      toast.error('Failed to load approval configs');
    } finally { setLoading(false); }
  };

  const taskMap = useMemo(() => {
    const m = {};
    tasks.forEach(t => { m[t.key] = t.label; });
    return m;
  }, [tasks]);

  const outletMap = useMemo(() => {
    const m = {};
    outlets.forEach(o => { m[o.id] = o.name; });
    return m;
  }, [outlets]);

  const grouped = useMemo(() => {
    const g = {};
    configs.forEach(c => {
      if (!g[c.task]) g[c.task] = [];
      g[c.task].push(c);
    });
    return Object.entries(g).sort(([a], [b]) => (taskMap[a] || a).localeCompare(taskMap[b] || b));
  }, [configs, taskMap]);

  const getUserLabel = (userId) => {
    const u = users.find(u => u.id === userId);
    return u ? `${u.full_name} (${u.role})` : '';
  };

  return (
    <div className="space-y-5" data-testid="shiftpro-settings-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">Approval Settings</h1>
          <p className="mt-1 text-base text-slate-600">View ShiftPro approval chains — managed via ShiftPro only</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
            <Settings className="h-4 w-4 text-amber-600" />
            <span className="text-sm font-semibold text-amber-700">{configs.length} Configs</span>
          </div>
        </div>
      </div>

      {/* Read-only notice */}
      <div className="flex items-center gap-3 px-4 py-3 bg-blue-50 border border-blue-200 rounded-xl">
        <ExternalLink className="h-5 w-5 text-blue-600 flex-shrink-0" />
        <div>
          <p className="text-sm font-semibold text-blue-800">Read-Only — Managed by ShiftPro</p>
          <p className="text-xs text-blue-600">Approval settings are configured directly in ShiftPro. Changes made there are reflected here automatically.</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div></div>
      ) : (
        <div className="space-y-4">
          {grouped.map(([taskKey, taskConfigs]) => {
            const isTaskOpen = expandedTask === taskKey;
            return (
              <div key={taskKey} className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
                <button
                  className="w-full px-5 py-3 bg-slate-800 text-white flex items-center gap-2 hover:bg-slate-700 transition-colors"
                  onClick={() => setExpandedTask(isTaskOpen ? null : taskKey)}
                >
                  {isTaskOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  <Users className="h-4 w-4" />
                  <span className="font-semibold text-sm">{taskMap[taskKey] || taskKey}</span>
                  <span className="text-xs text-slate-400 ml-1">({taskConfigs.length} config{taskConfigs.length > 1 ? 's' : ''})</span>
                </button>

                {isTaskOpen && (
                  <div>
                    <div className="grid grid-cols-12 gap-2 px-5 py-2 bg-slate-100 border-b border-slate-200 text-[10px] font-semibold text-slate-500 uppercase">
                      <div className="col-span-3">Location(s)</div>
                      <div className="col-span-7">Approval Chain</div>
                      <div className="col-span-2 text-center">Status</div>
                    </div>

                    <div className="divide-y divide-slate-100">
                      {taskConfigs.map(config => {
                        const approversSorted = (config.approvers || []).sort((a, b) => a.order - b.order);
                        return (
                          <div key={config.id} className="grid grid-cols-12 gap-2 px-5 py-3 items-center hover:bg-slate-50" data-testid={`config-${config.id}`}>
                            <div className="col-span-3">
                              <div className="flex items-center gap-1.5">
                                <MapPin className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                                <span className="text-xs font-semibold text-slate-800">
                                  {config.outlet_id ? (outletMap[config.outlet_id] || 'Unknown') : 'All Outlets'}
                                </span>
                              </div>
                            </div>
                            <div className="col-span-7">
                              <div className="flex items-center gap-1 flex-wrap">
                                {approversSorted.map((a, i) => (
                                  <React.Fragment key={i}>
                                    {i > 0 && <span className="text-slate-400 text-xs">→</span>}
                                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">
                                      {a.user_name || getUserLabel(a.user_id) || '?'}
                                    </span>
                                  </React.Fragment>
                                ))}
                              </div>
                            </div>
                            <div className="col-span-2 text-center">
                              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${config.is_active ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-slate-100 text-slate-500 border border-slate-200'}`}>
                                {config.is_active ? 'ACTIVE' : 'OFF'}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ShiftProSettings;
